import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, Zoom, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import SEO from '../../components/SEO/SEO';
import { productsAPI } from '../../services/api';
import { Product } from '../../types';
import { useCart } from '../../context/CartContext';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';
import 'swiper/css/free-mode';
import './ProductDetailPage.css';

const ProductDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { addItem } = useCart();

    const [product, setProduct] = useState<Product | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
    const [fullscreenImageIndex, setFullscreenImageIndex] = useState(0);

    useEffect(() => {
        const fetchProduct = async () => {
            if (id) {
                setLoading(true);
                try {
                    const response = await productsAPI.getById(parseInt(id), i18n.language);
                    const productData = response.data;

                    // Преобразуем данные с бэкенда в формат Product
                    const formattedProduct: Product = {
                        id: String(productData.id),
                        name: {
                            en: productData.name || '',
                            ru: productData.name || '',
                            kk: productData.name || ''
                        },
                        description: {
                            en: productData.description || '',
                            ru: productData.description || '',
                            kk: productData.description || ''
                        },
                        category: productData.category || '',
                        images: productData.images || [],
                        featured: productData.featured || false,
                        nutrition: productData.nutrition,
                        allergens: productData.allergens,
                        dietaryInfo: productData.dietaryInfo,
                        rating: productData.rating,
                        reviews: productData.reviews,
                        prepTime: productData.prepTime
                    };

                    setProduct(formattedProduct);
                } catch (error) {
                    console.error('Error fetching product:', error);
                    setProduct(undefined);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchProduct();
    }, [id, i18n.language]);

    if (loading) {
        return (
            <div className="product-detail-page-loading">
                <div className="spinner"></div>
                <p>{t('products.loading')}</p>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="product-detail-page-error">
                <h2>{t('products.product_not_found')}</h2>
                <button onClick={() => navigate('/products')}>{t('products.back_to_products')}</button>
            </div>
        );
    }

    const currentLang = i18n.language as 'en' | 'ru' | 'kk';
    const productName = product.name[currentLang];
    const productDescription = product.description[currentLang];

    const handleImageClick = (index: number) => {
        setFullscreenImageIndex(index);
        setIsFullscreenOpen(true);
    };

    const handleSendInquiry = () => {
        navigate('/contact', { state: { productName } });
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: productName,
                    text: productDescription,
                    url: window.location.href,
                });
            } catch (error) {
                console.log('Share cancelled');
            }
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert(t('products.link_copied'));
        }
    };

    return (
        <>
            <SEO
                title={`${productName} - ${t('site_name')}`}
                description={productDescription}
                image={product.images[0]}
            />

            <div className="product-detail-page">
                {/* Breadcrumbs */}
                <nav className="breadcrumbs">
                    <Link to="/">{t('home')}</Link>
                    <span className="separator">›</span>
                    <Link to="/products">{t('nav.products')}</Link>
                    <span className="separator">›</span>
                    <span className="current">{productName}</span>
                </nav>

                <div className="product-detail-container">
                    {/* Left Column - Image Gallery (60%) */}
                    <div className="product-gallery-column">
                        <Swiper
                            modules={[Navigation, Pagination, Thumbs, Zoom, FreeMode]}
                            spaceBetween={10}
                            navigation
                            pagination={{ clickable: true }}
                            thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                            zoom={true}
                            className="main-gallery"
                        >
                            {product.images.map((image, index) => (
                                <SwiperSlide key={index}>
                                    <div className="swiper-zoom-container" onClick={() => handleImageClick(index)}>
                                        <img src={image} alt={`${productName} ${index + 1}`} />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <Swiper
                            onSwiper={setThumbsSwiper}
                            modules={[Navigation, FreeMode, Thumbs]}
                            spaceBetween={10}
                            slidesPerView={4}
                            freeMode={true}
                            watchSlidesProgress={true}
                            className="thumb-gallery"
                        >
                            {product.images.map((image, index) => (
                                <SwiperSlide key={index}>
                                    <img src={image} alt={`${productName} thumbnail ${index + 1}`} />
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        {/* Trust Badges under gallery */}
                        <div className="gallery-trust-badges">
                            <div className="trust-item">
                                <span className="trust-icon">✓</span>
                                <span>{t('products.halal_certified')}</span>
                            </div>
                            <div className="trust-item">
                                <span className="trust-icon">🌿</span>
                                <span>{t('products.fresh_ingredients')}</span>
                            </div>
                            <div className="trust-item">
                                <span className="trust-icon">⭐</span>
                                <span>{t('products.premium_quality')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Sticky Product Info (40%) */}
                    <div className="product-purchase-panel">
                        <div className="panel-sticky-wrapper">
                            <h1 className="product-title">{productName}</h1>

                            {/* <div className="stock-status in-stock">
                                <span className="status-icon">✓</span>
                                <span>{t('products.in_stock')}</span>
                            </div> */}

                            <div className="short-description">
                                <p>{productDescription}</p>
                            </div>

                            {/* Action Buttons */}
                            <div className="panel-actions">
                                <button className="btn-add-cart-detail" onClick={() => {
                                    if (product) {
                                        const currentLang = i18n.language as 'en' | 'ru' | 'kk';
                                        addItem({
                                            productId: product.id,
                                            name: product.name[currentLang] || product.name['en'],
                                            category: product.category,
                                            image: product.images?.[0] || ''
                                        });
                                    }
                                }}>
                                    <span>{t('cart.add', 'Add to Cart')}</span>
                                </button>
                                <button className="btn-primary-action" onClick={handleSendInquiry}>
                                    <span>{t('products.send_inquiry')}</span>
                                </button>
                                <button className="btn-secondary-action" onClick={handleShare}>
                                    <span>{t('products.share', 'Share')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Production-ready concise section */}
                <div className="accordion-section">
                    <div className="accordion-container">
                        <div className="accordion-inner">
                            <h3 className="accordion-title">{t('products.product_description')}</h3>
                            <p>{productDescription}</p>
                            <p>{t('products.production_note')}</p>
                        </div>
                    </div>
                </div>

                {/* Fullscreen Image Modal */}
                <AnimatePresence>
                    {isFullscreenOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fullscreen-modal"
                            onClick={() => setIsFullscreenOpen(false)}
                        >
                            <button className="close-fullscreen" onClick={() => setIsFullscreenOpen(false)}>
                                ✕
                            </button>
                            <Swiper
                                modules={[Navigation, Pagination, Zoom]}
                                navigation
                                pagination={{ clickable: true }}
                                zoom={true}
                                initialSlide={fullscreenImageIndex}
                                className="fullscreen-gallery"
                            >
                                {product.images.map((image, index) => (
                                    <SwiperSlide key={index}>
                                        <div className="swiper-zoom-container">
                                            <img src={image} alt={`${productName} ${index + 1}`} />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

export default ProductDetailPage;
